import PropTypes from 'prop-types'
import { isNil, pathOr } from 'ramda'

import ActiveRecord from 'model/activeRecord'
import TicketState from 'model/tickets/ticketState'
import TicketType from 'model/tickets/ticketType'
import TicketAlarm from 'model/tickets/ticketAlarm'
import TicketFollowUpData from 'model/tickets/ticketFollowUpData'
import Interaction from 'model/tickets/interactions/interaction'
import TicketDeliverableData from 'model/tickets/ticketDeliverableData'
import AuditLog from 'model/tickets/auditLogs/auditLog'
import Member from 'model/person/member'
import User from 'model/person/user'
import Chat from 'model/chats/chat'
import Claim from 'model/claims/claim'
import CarePlan from 'model/carePlans/carePlan'
import Appointment from 'model/appointments/appointment'
import Mailorder from 'model/mailOrder/mailOrder'
import DiabetesProgram from 'model/diabetesProgram/diabetesProgram'
import WeightLossProgram from 'model/weightLossProgram/weightLossProgram'
import BenefitInquiryType from 'model/benefitInquiryType/benefitInquiryType'
import ProactiveDrugSearchType from 'model/drugSearch/drugSearch'
import AnnualPhysicalForm from 'model/wellness/annualPhysical'
import SeldonBeattieForm from 'model/wellness/seldonBeattie'
import GenericRequest from 'model/ticketSource/genericRequest/genericRequest'
import DrugListUpdateRequestType from 'model/drugListUpdateType/drugListUpdateType'
import PriorAuthorizationRequestType from 'model/priorAuthorizationStatusType/priorAuthorizationStatusType'
import PharmacyIdRequest from 'model/ticketSource/pharmacyIdRequest/pharmacyIdRequest'
import GeneralInquiry from 'model/ticketSource/generalInquiry/generalInquiry'
import NewMemberRequest from 'model/ticketSource/newMemberRequest/newMemberRequest'
import NavOnsiteRequest from 'model/ticketSource/navOnsiteRequest/navOnsiteRequest'
import NavReportRequest from 'model/ticketSource/navReportRequest/navReportRequest'
import NavOverviewDemoRequest from 'model/ticketSource/navOverviewDemoRequest/navOverviewDemoRequest'
import NavBenefitEducationRequest from 'model/ticketSource/navBenefitEducationRequest/navBenefitEducationRequest'
import NavCareRequest from 'model/ticketSource/navCareRequest/navCareRequest'
import NavClinicalSupportRequest from 'model/ticketSource/navClinicalSupportRequest/navClinicalSupportRequest'
import NavMemberServicesRequest from 'model/ticketSource/navMemberServicesRequest/navMemberServicesRequest'
import NavBillingAdvocacyRequest from 'model/ticketSource/navBillingAdvocacyRequest/navBillingAdvocacyRequest'
import ExistingNavRequest from 'model/ticketSource/existingNavRequest/existingNavRequest'
import FinancialDataValueType from 'model/financialDataValueType/financialDataValueType'
import RxCommonType from 'model/rxCommonType/rxCommonType'
import RxOverrideType from 'model/rxOverrideType/rxOverrideType'
import RxPriorAuthorizationType from 'model/rxPriorAuthorizationType/rxPriorAuthorizationType'
import AccumulatorIssueType from 'model/accumulatorIssueType/accumulatorIssueType'
import Task from 'model/tasks/task'
import IntakeTag from 'model/tag/tag'
import TicketMedicalNavigation from 'model/tickets/ticketMedicalNavigation'
import VendorSetupType from 'model/vendorSetupType/vendorSetupType'
import NewImplementationType from 'model/newImplementationType/newImplementationType'
import GroupTermingType from 'model/groupTermingType/groupTermingType'
import ClaimFileProcessingType from 'model/claimFileProcessingType/claimFileProcessingType'
import ImpactReportType from 'model/impactReportType/impactReportType'
import AccumulatorChange from 'model/planChangeHandling/accumulatorChange'
import FertilityMaximum from 'model/planChangeHandling/fertilityMaximum'
import GroupNumberChange from 'model/planChangeHandling/groupNumberChange'
import PlanChangeHandling from 'model/planChangeHandling/planChangeHandling'

import { getDateAndTimeFormat } from 'utils/helpers/dateHelper'
import { initArray } from 'model/helpers'

import {
  BILLING_ADVOCACY_TYPES,
  Ticket_Type_Code
} from 'constants/subjectTypeConstants'
import EligibilityInquiryType from 'model/eligibilityInquiryType/eligibilityInquiryType'

const CHAT_TYPENAME = 'Chat'
const CLAIM_TYPENAME = 'ClaimV2'
const CARE_PLAN_TYPENAME = 'RoadmapV2'
const APPOINTMENT_TYPENAME = 'Appointment'
const BENEFIT_INQUIRY_TYPENAME = 'BenefitInquiryType'
const MAILORDER = 'MailOrderTransferType'
const DIABETES_PROGRAM_TYPENAME = 'DiabetesProgramType'
const WEIGHT_LOSS_PROGRAM_TYPENAME = 'WeightManagementProgramType'
const ANNUAL_PHYSICAL_TYPENAME = 'AnnualPhysicalFormType'
const SELDON_BEATTIE_TYPENAME = 'SeldonBeattieFormType'
const DRUG_SEARCH_TYPENAME = 'ProactiveDrugSearchType'
const MEDICAL_NAVIGATION_TYPE = 'MedicalNavigationType'
const GENERIC_REQUEST_TYPE = 'GenericRequestType'
const PHARMACY_ID_REQUEST_TYPE = 'PharmacyIdRequestType'
const GENERAL_INQUIRY_TYPE = 'GeneralInquiryType'
const NEW_MEMBER_REQUEST_TYPE = 'NewMemberRequestType'
const NAV_BENEFIT_EDUCATION_REQUEST_TYPE = 'NavBenefitEducationRequestType'
const NAV_CARE_REQUEST_TYPE = 'NavCareRequestType'
const NAV_CLINICAL_SUPPORT_REQUEST_TYPE = 'NavClinicalSupportRequestType'
const NAV_MEMBER_SERVICES_REQUEST_TYPE = 'NavMemberServicesRequestType'
const NAV_BILLING_ADVOCACY_REQUEST_TYPE = 'NavBillingAdvocacyRequestType'
const EXISTING_NAV_REQUEST_TYPE = 'ExistingNavRequestType'
const FINANCIAL_VALUE_TYPE = 'FinancialDataValueType'
const RX_COMMON_TYPE = 'RxCommonType'
const RX_OVERRIDE_TYPE = 'RxOverrideType'
const RX_PRIOR_AUTHORIZATION_TYPE = 'RxPriorAuthorizationType'
const DRUG_LIST_UPDATE_TYPE = 'DrugListUpdateRequestType'
const PRIOR_AUTHORIZATION_STATUS_TYPE = 'PriorAuthorizationStatusType'
const ACCUMULATOR_ISSUE_TYPE = 'AccumulatorIssueType'
const NAV_ONSITE_REQUEST_TYPE = 'NavOnsiteRequestType'
const NAV_REPORT_REQUEST_TYPE = 'NavReportRequestType'
const NAV_OVERVIEW_DEMO_REQUEST_TYPE = 'NavOverviewDemoRequestType'
const NAV_GENERAL_INQUIRY_TYPE = 'NavGeneralInquiryType'
const VENDOR_SETUP_TYPE = 'VendorSetupType'
const NEW_IMPLEMENTATION_TYPE = 'NewImplementationType'
const GROUP_TERMING_TYPE = 'GroupTermingType'
const CLAIM_FILE_PROCESSING_TYPE = 'ClaimFileProcessingType'
const IMPACT_REPORT_TYPE = 'ImpactReportType'
const ACCUMULATOR_CHANGE_TYPE = 'AccumulatorChangeType'
const FERTILITY_MAXIMUM_TYPE = 'FertilityMaximumType'
const GROUP_NUMBER_CHANGE_TYPE = 'GroupNumberChangeType'
const PLAN_CHANGE_HANDLING_TYPE = 'PlanChangeHandlingType'
const ELIGIBILITY_INQUIRY_TYPE = 'EligibilityInquiryType'

const enumResolver = entity => {
  // console.log('enumResolver', entity)
  if (isNil(entity)) return null
  const typename = entity.__typename
  switch (typename) {
    case ACCUMULATOR_ISSUE_TYPE:
      return new AccumulatorIssueType(entity)
    case CHAT_TYPENAME:
      return new Chat(entity)
    case CLAIM_TYPENAME:
      return new Claim(entity)
    case CARE_PLAN_TYPENAME:
      return new CarePlan(entity)
    case BENEFIT_INQUIRY_TYPENAME:
      return new BenefitInquiryType(entity)
    case APPOINTMENT_TYPENAME:
      return new Appointment(entity)
    case MAILORDER:
      return new Mailorder(entity)
    case DIABETES_PROGRAM_TYPENAME:
      return new DiabetesProgram(entity)
    case DRUG_LIST_UPDATE_TYPE:
      return new DrugListUpdateRequestType(entity)
    case WEIGHT_LOSS_PROGRAM_TYPENAME:
      return new WeightLossProgram(entity)
    case ANNUAL_PHYSICAL_TYPENAME:
      return new AnnualPhysicalForm(entity)
    case SELDON_BEATTIE_TYPENAME:
      return new SeldonBeattieForm(entity)
    case DRUG_SEARCH_TYPENAME:
      return new ProactiveDrugSearchType(entity)
    case MEDICAL_NAVIGATION_TYPE:
      return new TicketMedicalNavigation(entity)
    case PHARMACY_ID_REQUEST_TYPE:
      return new PharmacyIdRequest(entity)
    case GENERIC_REQUEST_TYPE:
      return new GenericRequest(entity)
    case NEW_MEMBER_REQUEST_TYPE:
      return new NewMemberRequest(entity)
    case NAV_BENEFIT_EDUCATION_REQUEST_TYPE:
      return new NavBenefitEducationRequest(entity)
    case NAV_CARE_REQUEST_TYPE:
      return new NavCareRequest(entity)
    case NAV_CLINICAL_SUPPORT_REQUEST_TYPE:
      return new NavClinicalSupportRequest(entity)
    case NAV_MEMBER_SERVICES_REQUEST_TYPE:
      return new NavMemberServicesRequest(entity)
    case NAV_BILLING_ADVOCACY_REQUEST_TYPE:
      return new NavBillingAdvocacyRequest(entity)
    case EXISTING_NAV_REQUEST_TYPE:
      return new ExistingNavRequest(entity)
    case GENERAL_INQUIRY_TYPE:
      return new GeneralInquiry(entity)
    case FINANCIAL_VALUE_TYPE:
      return new FinancialDataValueType(entity)
    case RX_COMMON_TYPE:
      return new RxCommonType(entity)
    case RX_OVERRIDE_TYPE:
      return new RxOverrideType(entity)
    case RX_PRIOR_AUTHORIZATION_TYPE:
      return new RxPriorAuthorizationType(entity)
    case PRIOR_AUTHORIZATION_STATUS_TYPE:
      return new PriorAuthorizationRequestType(entity)
    case NAV_ONSITE_REQUEST_TYPE:
      return new NavOnsiteRequest(entity)
    case NAV_REPORT_REQUEST_TYPE:
      return new NavReportRequest(entity)
    case NAV_OVERVIEW_DEMO_REQUEST_TYPE:
      return new NavOverviewDemoRequest(entity)
    case NAV_GENERAL_INQUIRY_TYPE:
      return new GeneralInquiry(entity)
    case VENDOR_SETUP_TYPE:
      return new VendorSetupType(entity)
    case NEW_IMPLEMENTATION_TYPE:
      return new NewImplementationType(entity)
    case GROUP_TERMING_TYPE:
      return new GroupTermingType(entity)
    case CLAIM_FILE_PROCESSING_TYPE:
      return new ClaimFileProcessingType(entity)
    case IMPACT_REPORT_TYPE:
      return new ImpactReportType(entity)
    case ACCUMULATOR_CHANGE_TYPE:
      return new AccumulatorChange(entity)
    case FERTILITY_MAXIMUM_TYPE:
      return new FertilityMaximum(entity)
    case GROUP_NUMBER_CHANGE_TYPE:
      return new GroupNumberChange(entity)
    case PLAN_CHANGE_HANDLING_TYPE:
      return new PlanChangeHandling(entity)
    case ELIGIBILITY_INQUIRY_TYPE:
      return new EligibilityInquiryType(entity)
    default:
      return null
  }
}

class Ticket extends ActiveRecord {
  // named after the GraphQL scheme
  constructor(init) {
    super(init)
    // console.log('> Tickets constructor -', init)
    if (isNil(init)) return undefined
    this._alarm = new TicketAlarm(init.alarm)
    this._archived = init.archived
    this._assigned_to = new User(init.assigned_to)
    this._audit_logs = initArray(init, ['audit_logs'], AuditLog)
    this._chat = new Chat(init.chat)
    this._chats = init.chats ? init.chats.map(item => new Chat(item)) : []
    this._deliverables = init.deliverables
      ? init.deliverables.map(item => new TicketDeliverableData(item))
      : []
    this._has_deliverables = init.has_deliverables
    this._member = new Member(init.member)
    this._flag = init.flag
    this._follow_up = new TicketFollowUpData(init.follow_up)
    this._hours_in_current_state = init.hours_in_current_state
    this._interactions = pathOr(false, ['interactions', 'nodes'], init)
      ? init.interactions.nodes.map(item => new Interaction(item))
      : []
    this._initial_interaction = new Interaction(init.initial_interaction)
    this._interactions_count = init.interactions_count
    this._next_task = init.next_task
    this._notes = init.notes
    this._parent_ticket = init.parent_ticket
    // TODO resource class
    // Workaround until we'll have models for all possible cases
    this._priority = init.priority
    this._urgency = init.urgency
    this._reopened_at = init.reopened_at
    this._closed_at = init.closed_at
    this._last_interaction = init.last_interaction
    this._resource = enumResolver(init.resource)
    this._tasks = initArray(init, ['tasks'], Task)
    this._type = new TicketType(init.type)
    this._title = init.title // Unused?
    this._state = new TicketState(init.state)
    this._survey = init.survey

    this._benefit_categories = init.benefit_categories
    this._intake_queue = init.intake_queue
    this._intake_tags = initArray(init, ['intake_tags'], IntakeTag)
    this._intake_tag_snapshots = init.intake_tag_snapshots
  }

  static shape = {
    ...ActiveRecord.shape,
    alarm: PropTypes.instanceOf(TicketAlarm),
    archived: PropTypes.bool,
    assigned_to: PropTypes.instanceOf(User),
    audit_logs: PropTypes.array,
    chat: PropTypes.instanceOf(Chat),
    chats: PropTypes.arrayOf(PropTypes.instanceOf(Chat)),
    deliverables: PropTypes.arrayOf(
      PropTypes.instanceOf(TicketDeliverableData)
    ),
    has_deliverables: PropTypes.bool,
    member: PropTypes.instanceOf(Member),
    follow_up: PropTypes.instanceOf(TicketFollowUpData),
    flag_id: PropTypes.number,
    hours_in_current_state: PropTypes.number,
    interactions: PropTypes.arrayOf(PropTypes.instanceOf(Interaction)),
    interactions_count: PropTypes.number,
    initial_interaction: PropTypes.instanceOf(Interaction),
    next_task: PropTypes.object,
    parent_ticket: PropTypes.object,
    notes: PropTypes.string,
    priority: PropTypes.string,
    reopened_at: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    closed_at: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    last_interaction: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    tasks: PropTypes.arrayOf(PropTypes.instanceOf(Task)),
    type: PropTypes.instanceOf(TicketType),
    title: PropTypes.string,
    state: PropTypes.instanceOf(TicketState),
    survey: PropTypes.object,
    benefit_categories: PropTypes.array,
    intake_queue: PropTypes.object,
    intake_tags: PropTypes.arrayOf(PropTypes.instanceOf(IntakeTag)),
    intake_tag_snapshots: PropTypes.arrayOf(PropTypes.instanceOf(IntakeTag))
  }

  // Getters and setters for class fields
  get alarm() {
    return this._alarm
  }

  get archived() {
    return this._archived
  }

  get assigned_to() {
    return this._assigned_to
  }

  get audit_logs() {
    return this._audit_logs
  }

  get chat() {
    return this._chat
  }

  get chats() {
    return this._chats
  }

  get deliverables() {
    return this._deliverables || []
  }

  set deliverables(items) {
    this._deliverables = items?.map(item => new TicketDeliverableData(item))
  }

  get has_deliverables() {
    return this._has_deliverables || false
  }

  get member() {
    return this._member
  }

  get flag() {
    return this._flag
  }

  get follow_up() {
    return this._follow_up
  }

  get hours_in_current_state() {
    return this._hours_in_current_state || 0
  }

  get intake_tags() {
    return this._intake_tags || []
  }

  get intake_tag_snapshots() {
    return this._intake_tag_snapshots || []
  }

  get interactions() {
    return this._interactions
  }

  get interactions_count() {
    return this._interactions_count
  }

  get initial_interaction() {
    return this._initial_interaction
  }

  get next_task() {
    return this._next_task
  }

  get notes() {
    return this._notes || ''
  }

  get parent_ticket() {
    return this._parent_ticket
  }

  get priority() {
    return this._priority
  }

  get urgency() {
    return this._urgency
  }

  get resource() {
    return this._resource || {}
  }

  get reopened_at() {
    return this._reopened_at || false
  }

  get closed_at() {
    return this._closed_at || false
  }

  get last_interaction() {
    return this._last_interaction || false
  }

  get tasks() {
    return this._tasks || []
  }

  get type() {
    return this._type
  }

  get title() {
    return this._title
  }

  get state() {
    return this._state
  }

  get survey() {
    return this._survey || {}
  }

  get benefit_categories() {
    return this._benefit_categories || []
  }

  get intake_queue() {
    return this._intake_queue
  }

  // Calculable
  get getCreated() {
    return getDateAndTimeFormat(this._created_at)
  }

  get getUpdated() {
    return getDateAndTimeFormat(this._updated_at)
  }

  get getReopenedAt() {
    return this.reopened_at && getDateAndTimeFormat(this.reopened_at)
  }

  get getClosedAt() {
    return this.closed_at && getDateAndTimeFormat(this.closed_at)
  }

  get getLastInteraction() {
    return this.last_interaction && getDateAndTimeFormat(this.last_interaction)
  }

  get getInteractionName() {
    return pathOr(
      'N/A',
      ['initial_interaction', 'interaction_modality', 'name'],
      this
    )
  }

  get getDirection() {
    return pathOr('N/A', ['initial_interaction', 'direction'], this)
  }

  get getTicketTitle() {
    if (this.state?.isPending) return 'Pending Ticket'
    if (this.state?.isInProgress) return 'In Progress Ticket'
    if (this.state?.isReadyToClose) return 'Ready To Close Ticket'
    if (this.state?.isClosed) return 'Closed Ticket'
    return 'Ticket Details'
  }

  get getTicketDescription() {
    const { code, display_name: subject } = this.type

    const assigned_entity = pathOr({}, ['resource', 'assigned_entity'], this)

    let title
    let tooltipStrings
    let shouldUseTooltip = false

    switch (code) {
      case Ticket_Type_Code.Appointment:
        shouldUseTooltip = true
        tooltipStrings = assigned_entity.isFacility
          ? pathOr([], ['display_location_types'], assigned_entity)
          : pathOr([], ['specialties'], assigned_entity).map(i => i.name)

        break
      case Ticket_Type_Code.FindCare:
        title = this.resource.title
        break
      case Ticket_Type_Code.MedicalNavigation:
        title = this.resource.symptom_name
        break
      default:
        if (BILLING_ADVOCACY_TYPES.includes(code)) {
          title = this.resource.claim_category_name
        }
        break
    }

    return {
      shouldUseTooltip,
      tooltipStrings,
      title,
      subject
    }
  }

  get getRemainingTasks() {
    return this.tasks.filter(i => !i.is_completed).length
  }

  get getCompletedTasks() {
    return this.tasks.filter(i => i.is_completed).length
  }

  get shouldBlockTicketUI() {
    return this.state.isClosed || this.archived
  }

  get assigneeNameOrQueue() {
    const queueName = this.intake_queue?.name
    const assigneeName = this.assigned_to?.id
      ? this.assigned_to?.getFullName
      : 'First Available Navigator'
    return queueName || assigneeName
  }
}

export default Ticket
